/**
 * Whether a stored provider token row holds a connected account. Pure and
 * import-free, so the Control Plane, the sync engine and the client read one
 * definition.
 */

/**
 * The link status an owner Disconnect (the account-configuration `reject`
 * action) leaves on a row. The row keeps its identity, sync history and
 * selection so imported data stays attributable, but its credentials are
 * gone and it stays disconnected until the owner connects again.
 */
export const PROVIDER_ACCOUNT_OWNER_DISCONNECTED_LINK_STATUS = "owner_rejected";

/**
 * Stored secrets that authorize provider access. Webhook secrets only verify
 * inbound calls, so installing one does not connect an account.
 */
export const PROVIDER_ACCOUNT_ACCESS_CREDENTIAL_FIELDS = [
  "token",
  "refreshToken",
  "botToken",
  "pairingSecret",
  "appSecret",
] as const;

function accountRow(token: unknown): Record<string, unknown> | null {
  return token && typeof token === "object" && !Array.isArray(token)
    ? token as Record<string, unknown>
    : null;
}

/**
 * The owner disconnected this account. The token-state policy deleted its
 * credentials on reject and kept the row only for its immutable identity
 * binding, so its leftover `expiresIn` and watermarks describe nothing that
 * can be read or refreshed until the account is connected again.
 *
 * Reconnecting installs a new credential and clears `owner_rejected`. A row
 * reconnected before that rule existed still carries the marker next to its
 * new credential, so the marker counts only while no access credential is
 * stored.
 */
export function isOwnerDisconnectedProviderAccount(token: unknown): boolean {
  const row = accountRow(token);
  if (row?.linkStatus !== PROVIDER_ACCOUNT_OWNER_DISCONNECTED_LINK_STATUS) return false;
  return !PROVIDER_ACCOUNT_ACCESS_CREDENTIAL_FIELDS.some(
    (field) => typeof row[field] === "string" && row[field] !== "",
  );
}

/**
 * The row carries a credential, a sync history or a provider identity. A row
 * that needs reauthorization still holds its account; callers that want only
 * usable accounts also check `reauthorizeRequired`. A row the owner
 * disconnected holds no account, whatever identity or history it kept.
 */
export function hasProviderAccountConnectionEvidence(token: unknown): boolean {
  const row = accountRow(token);
  if (!row || isOwnerDisconnectedProviderAccount(row)) return false;
  return Boolean(
    row.token
    || row.refreshToken
    || (typeof row.expiresIn === "number" && Number.isFinite(row.expiresIn) && row.expiresIn > 0)
    || row.isSynced
    || row.helperConnected
    || row.linkStatus === "linked"
    || row.providerAccountKey
    || row.providerAccountId,
  );
}

/**
 * When the account was connected, or null when the row carries no stamp. The
 * Control Plane stamps every account that connects (`providerTokenState.ts`).
 */
export function providerAccountConnectedAt(row: unknown): number | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const value = (row as Record<string, unknown>).connectedAt;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

export type ProviderAccountRowConnectionState = "connected" | "reauthorize" | "disconnected";

/**
 * What a settings card shows and does for one stored row: connected
 * (Disconnect), reauthorize (Reconnect) or disconnected (Connect). Client
 * rows carry no secrets, so any stored row the owner did not disconnect holds
 * its account. An owner disconnect wins over a stale reauthorization flag,
 * because the owner chose to stop the account rather than the provider
 * revoking it. Listing an account additionally needs connection evidence.
 */
export function providerAccountRowConnectionState(
  token: unknown,
): ProviderAccountRowConnectionState {
  const row = accountRow(token);
  if (!row || isOwnerDisconnectedProviderAccount(row)) return "disconnected";
  if (row.reauthorizeRequired || row.needReAuthorization) return "reauthorize";
  return "connected";
}

/**
 * The row is an account a read or sync can use now: connected (not owner
 * disconnected, no pending reauthorization) and holding connection evidence.
 * The one definition behind every "connected accounts" list.
 */
export function isUsableProviderAccountRow(token: unknown): boolean {
  return providerAccountRowConnectionState(token) === "connected"
    && hasProviderAccountConnectionEvidence(token);
}

/** The row is one of the provider's accounts: listed as its own settings card. */
export function isListedProviderAccountRow(token: unknown): boolean {
  const state = providerAccountRowConnectionState(token);
  return state === "reauthorize"
    || (state === "connected" && hasProviderAccountConnectionEvidence(token));
}

export type ProviderCardPrimaryAction = "connect" | "reauthorize" | "update_access" | "disconnect";

/**
 * What the card's main button does, matching its label. A disconnected row
 * (none stored, or the owner disconnected it) always starts a sign-in, so its
 * Connect never removes the row and the data it kept.
 */
export function providerCardPrimaryAction(args: {
  rowState: ProviderAccountRowConnectionState;
  selectionHasChanges: boolean;
  configurationBlocksAuthorization: boolean;
}): ProviderCardPrimaryAction {
  if (args.rowState === "disconnected") return "connect";
  // Sign-in is unavailable, so a stored row can only be disconnected.
  if (args.configurationBlocksAuthorization) return "disconnect";
  if (args.rowState === "reauthorize") return "reauthorize";
  return args.selectionHasChanges ? "update_access" : "disconnect";
}

/**
 * A provider toggle removes the stored connection only for a row that still
 * holds one, and never while reconnecting or adding another account.
 */
export function providerToggleRemovesConnection(
  existingRow: unknown,
  options?: { forceReconnect?: boolean; addAccount?: boolean },
): boolean {
  return providerAccountRowConnectionState(existingRow) !== "disconnected"
    && !options?.forceReconnect
    && !options?.addAccount;
}

export type UsableProviderAccountRow = {
  provider: string;
  /** The session key that stores the account; reads, ledgers and disconnects all name it. */
  providerAccountKey: string;
  row: Record<string, unknown>;
};

/**
 * The session's usable account rows for the given providers. A provider's
 * first account is stored under the bare provider key (`youtube`) and later
 * accounts under scoped keys (`youtube:<id>`), so both shapes are accounts.
 * Only usable rows (`isUsableProviderAccountRow`) are listed, the same rule
 * `getConnectedProviderAccounts` advertises sources by.
 */
export function listUsableProviderAccountRows(
  sessionTokens: unknown,
  providers: ReadonlySet<string> | readonly string[],
): UsableProviderAccountRow[] {
  const tokens = accountRow(sessionTokens);
  if (!tokens) return [];
  const wanted = providers instanceof Set ? providers : new Set(providers);
  return Object.entries(tokens).flatMap(([key, token]) => {
    const row = accountRow(token);
    if (!row || !isUsableProviderAccountRow(row)) return [];
    const separator = key.indexOf(":");
    const provider = (separator === -1 ? key : key.slice(0, separator)).trim();
    // `provider:` names no account.
    if (!provider || separator === key.length - 1 || !wanted.has(provider)) return [];
    return [{ provider, providerAccountKey: key, row }];
  });
}
